import {
  ChangeDetectorRef,
  Component,
  inject,
  ViewChild,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';

import { TemplatePortal, CdkPortalOutlet } from '@angular/cdk/portal';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { faGear } from '@fortawesome/free-solid-svg-icons';

import { PortalRegistryService } from 'src/app/core/services/portal-registry.service';

import { FormEditorDialogBase } from 'src/app/shared/abstract/form-editor-dialog-base';

import { User } from 'src/app/core/models/core.models';
import { NomObjectClass } from 'src/app/core/constants';
import { getIconForDataObject } from 'src/app/shared/utils/object-utils';
import {
  SuiDialogConfig,
  SuiDialogTabsComponent,
  SuiDialogLeftHeaderSlot,
  SuiDialogLeftContentSlot,
  SuiDialogRightHeaderSlot,
  SuiDialogRightContentSlot,
  SuiDialogRightFooterSlot,
} from '@sui/dialog';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SuiButtonComponent } from '@sui/button';
import { EditorUserComponent } from '../editor-user/editor-user.component';

interface DialogData {
  user: User | null;
  companyId?: string | null;
}
interface DialogOutput {}

@Component({
  selector: 'app-editor-user-dialog',
  templateUrl: './editor-user-dialog.component.html',
  styleUrl: './editor-user-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SuiDialogTabsComponent,
    SuiDialogLeftHeaderSlot,
    FaIconComponent,
    SuiDialogLeftContentSlot,
    CdkPortalOutlet,
    SuiDialogRightHeaderSlot,
    SuiButtonComponent,
    SuiDialogRightContentSlot,
    SuiDialogRightFooterSlot,
    forwardRef(() => EditorUserComponent),
    TranslatePipe,
  ],
})
export class EditorUserDialogComponent extends FormEditorDialogBase<
  any,
  DialogData,
  DialogOutput
> {
  static dialogConfig: Partial<SuiDialogConfig> = {
    width: '70vw',
    minWidth: 800,
    maxWidth: 1020,
  };
  @ViewChild('editorUser')
  protected override editorComponent!: any;

  private cdr = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);
  private portalRegistry: PortalRegistryService = inject(PortalRegistryService);

  public tabsPortal!: TemplatePortal;
  public formPortal!: TemplatePortal;

  actionIcon = faGear;
  actionTitle = this.dialogData.user
    ? this.translateService.instant('popup.action-title.edit-user-settings')
    : this.translateService.instant('popup.action-title.create-user');
  objectIcon = getIconForDataObject(NomObjectClass.USER);
  objectTitle = '';

  get user() {
    return this.dialogData.user as User | null;
  }

  get companyId() {
    return this.dialogData.companyId || null;
  }

  setupUiState(): void {
    if (this.user) {
      this.objectTitle = this.user.display_name;
    }
  }

  ngOnInit(): void {
    this.setupUiState();
  }

  ngAfterViewInit(): void {
    this.tabsPortal = this.portalRegistry.get('general-form-editor-tabs');
    this.formPortal = this.portalRegistry.get('general-form-editor-form');

    // ! Setup dirty-close protection
    this.initializeFormEditorProtection();

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.portalRegistry.clearAll();
  }

  onTitleChange(title: string): void {}

  async cancelAction(): Promise<void> {
    await this.cancel();
  }
}
